/*
 *  TTTech nerve-management-system-frontend
 *  Copyright(c) 2022. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */

import NodeBasicInfoModel from '@/model/node/nodeBasicInfo.model';
import DeployedWorkloadModel from '@/model/node/sub-models/deployed-workload.model';
import DeployedComposeWorkloadModel from '@/model/node/sub-models/deployed-compose-workload.model';
import { isNoPrereleasedVersionGreaterThanOrEqualTo } from '@/helpers/shared';

export const NODE_MODELS = {
  MFN100: 'mfn-100',
};

export const CONNECTION_STATUSES = {
  ONLINE: 'online',
  OFFLINE: 'offline',
};

function toLocaleDate(value) {
  if (!value) {
    return '';
  }
  if (String(value).includes('/')) {
    return value;
  }
  const date = new Date(Number.isNaN(Number(value)) ? value : Number(value));
  return date.toLocaleString('en-GB');
}

function normalizeDnaStatus(value) {
  if (!value) return 'n/a';
  const normalized = String(value).toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export default class NodeModel extends NodeBasicInfoModel {
  /**
   * Creates an instance of NodeModel.
   * @param {Object} data - NodeModel
   * @class NodeModel
   * @property {string} secureBootStatus - Node secureBootStatus
   * @property {string} connectionStatus - Node connectionStatus
   * @property {string} version - Node version
   * @property {string} model - Node model
   */
  constructor(data = {}) {
    super(data);
    this.secureBootStatus = data.secureBootStatus;
    this.connectionStatus = data.connectionStatus || '';
    if (data.model) {
      this.model = data.model || NODE_MODELS.MFN100;
    }
    this.userId = data.userId || '';
    this.created = data;
    if (data.ip_address || data.wanAddress || data.wanIpAddress) {
      // hack for preserving wan address
      this.wanAddress = data.ip_address || data.wanAddress || data.wanIpAddress || '';
    }
    this.cpu = this.isOnline() ? data.cpu : 0;
    this.docker = this.isOnline() ? data.docker || {} : {};
    this.lvm = this.isOnline() ? data.lvm || {} : {};
    this.mem = this.isOnline() ? data.mem || {} : {};
    if (data.workloadStats && Array.isArray(data.workloadStats) && data.workloadStats.length !== 0) {
      const devices = [];
      data.devices.forEach((wlDevice) => {
        let foundStats = false;
        data.workloadStats.forEach((wlStats) => {
          if (wlDevice.id === wlStats.deviceId) {
            foundStats = true;
            if (
              (wlDevice._type === 'dockerCompose' || wlDevice._type === 'Docker Compose') &&
              isNoPrereleasedVersionGreaterThanOrEqualTo(data.currentFWVersion, '2.7.0')
            ) {
              devices.push(new DeployedComposeWorkloadModel(Object.assign(wlDevice, { wlStats })));
            } else {
              devices.push(new DeployedWorkloadModel(Object.assign(wlDevice, { wlStats })));
            }
          }
        });
        if (!foundStats) {
          if (
            (wlDevice._type === 'dockerCompose' || wlDevice._type === 'Docker Compose') &&
            isNoPrereleasedVersionGreaterThanOrEqualTo(data.currentFWVersion, '2.7.0')
          ) {
            devices.push(new DeployedComposeWorkloadModel(Object.assign(wlDevice)));
          } else {
            devices.push(new DeployedWorkloadModel(Object.assign(wlDevice)));
          }
        }
      });
      this.devices = devices;
    } else {
      this.devices = data.devices
        ? data.devices.map((d) =>
            (d.type === 'docker-compose' || d._type === 'Docker Compose') &&
            isNoPrereleasedVersionGreaterThanOrEqualTo(data.currentFWVersion, '2.7.0')
              ? new DeployedComposeWorkloadModel(d)
              : new DeployedWorkloadModel(d),
          )
        : [];
    }
    this.lastSystemStart = toLocaleDate(data.lastSystemStart);
    this.capabilities = data.capabilities || [];
    this.vmSnapshot = data.vmSnapshot || false;
    this.vmBackup = data.vmBackup || false;
    this.rcApproval = data.rcApproval || 0;
    this.productionMode = !!data.productionMode;

    this.dnaWorkloadStatus = normalizeDnaStatus(data.dnaWorkloadStatus);
    this.dnaNodeStatus = normalizeDnaStatus(data.dnaNodeStatus);
    this.dnaNodeMetadata = data.dnaNodeMetadata || [];
    this.dnaWorkloadMetadata = data.dnaWorkloadMetadata || [];

    this.lastSeen = toLocaleDate(data.heartBeatReceived) || data.lastSeen || '';

    this.timezone = data.timezone?.name ? { ...data.timezone } : undefined;
  }

  set created(data) {
    if (data.created) {
      this._created = data.created.includes('/') ? data.created : new Date(data.created).toLocaleString('en-GB');
      return;
    }
    this._created = data._created;
  }

  get created() {
    return this._created;
  }

  isOnline() {
    return this.connectionStatus === CONNECTION_STATUSES.ONLINE;
  }

  get filterVMs() {
    return this.devices
      .reduce((list, current) => {
        if (!!current.id && current.type === 'vm') {
          list.push({
            name: current.name,
            deviceId: current.id,
            display: current.videoOutput.enabled,
            sequence: current.videoOutput.sequence,
          });
        }
        return list;
      }, [])
      .sort((w1, w2) => w1.sequence - w2.sequence);
  }
}
